import React from "react";

/**
 * Two-handle price filter over the hotels offered for one night.
 *
 * The figures are the full per-person trip price with that hotel chosen, not the hotel's own room
 * cost - it is the number the customer is actually quoted, and the only one we can state exactly
 * once margin and rounding are applied.
 *
 * Filtering never removes the hotel already selected. Losing your own choice because you nudged a
 * slider is worse than showing one row outside the range.
 */
export default function HotelPriceRangeFilter({
  bounds,
  value,
  onChange,
  shownCount,
  totalCount,
  formatAmount,
}) {
  const { min, max } = bounds || {};
  if (min == null || max == null || max <= min) return null;

  const [low, high] = value;
  // A round step keeps the handles landing on sensible figures rather than arbitrary rupees.
  const step = Math.max(50, Math.round((max - min) / 40 / 50) * 50);
  const percent = (amount) => ((amount - min) / (max - min)) * 100;

  const setLow = (next) => onChange([Math.min(Number(next), high), high]);
  const setHigh = (next) => onChange([low, Math.max(Number(next), low)]);

  return (
    <div className="mb-2">
      <div className="d-flex justify-content-between align-items-center mb-1">
        <div className="small fw-semibold">Price range</div>
        <div className="small text-muted">
          {shownCount} of {totalCount} hotels
        </div>
      </div>
      <div className="small text-muted mb-1">Total per person, including this hotel</div>
      <div className="trip-price-range">
        <div className="trip-price-range__track" />
        <div
          className="trip-price-range__fill"
          style={{ left: `${percent(low)}%`, right: `${100 - percent(high)}%` }}
        />
        <input
          type="range"
          min={min}
          max={max}
          step={step}
          value={low}
          onChange={(event) => setLow(event.target.value)}
          aria-label="Minimum price per person"
        />
        <input
          type="range"
          min={min}
          max={max}
          step={step}
          value={high}
          onChange={(event) => setHigh(event.target.value)}
          aria-label="Maximum price per person"
        />
      </div>
      <div className="d-flex justify-content-between align-items-center">
        <span className="trip-price-range__value">Rs. {formatAmount(low)}</span>
        <span className="trip-price-range__value">
          Rs. {formatAmount(high)}
          {high >= max ? "+" : ""}
        </span>
      </div>
    </div>
  );
}
